import { useEffect, useMemo, useRef, useState } from 'react';
import { Outlet, NavLink, useNavigate, useSearchParams, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  useLogout,
  useSettings,
  useUpdateSettings,
  useFeeds,
  useClusters,
  useMutes,
  useAddMute,
  useDeleteMute,
  useInvites,
  useMe,
} from '../api/queries.js';
import { feedLayout as feedLayoutSchema } from '@b4b/shared';
import { useTopicLabel } from '../lib/topics.js';
import { applyTheme, getStoredTheme } from '../theme.js';

const navItems = [
  { to: '/', key: 'nav.feed', icon: '📰' },
  { to: '/saved', key: 'nav.saved', icon: '⭐' },
  { to: '/sources', key: 'nav.sources', icon: '📡' },
  { to: '/search', key: 'nav.search', icon: '🔍' },
  { to: '/settings', key: 'nav.settings', icon: '⚙️' },
] as const;

/** Header hides while scrolling down and reappears on the first scroll up.
 * Listens to window scroll (regular pages) and the custom event dispatched
 * by the reels container (which scrolls internally). */
function useAutoHideHeader(disabled: boolean): boolean {
  const [hidden, setHidden] = useState(false);
  const lastY = useRef(0);

  useEffect(() => {
    if (disabled) {
      setHidden(false);
      return;
    }
    const onScrollY = (y: number) => {
      const delta = y - lastY.current;
      if (Math.abs(delta) < 10) return;
      setHidden(delta > 0 && y > 56);
      lastY.current = y;
    };
    const onWindowScroll = () => onScrollY(window.scrollY);
    const onCustomScroll = (e: Event) => onScrollY((e as CustomEvent<{ top: number }>).detail.top);
    window.addEventListener('scroll', onWindowScroll, { passive: true });
    window.addEventListener('b4b:scroll', onCustomScroll);
    return () => {
      window.removeEventListener('scroll', onWindowScroll);
      window.removeEventListener('b4b:scroll', onCustomScroll);
    };
  }, [disabled]);

  return hidden;
}

export default function Layout() {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const logout = useLogout();
  const me = useMe();
  const isAdmin = me.data?.user.isAdmin === true;
  const settings = useSettings();
  const updateSettings = useUpdateSettings();
  const topicLabel = useTopicLabel();
  const feeds = useFeeds();
  const [searchParams] = useSearchParams();
  const [menuOpen, setMenuOpen] = useState(false);
  const mutes = useMutes();
  const addMute = useAddMute();
  const deleteMute = useDeleteMute();
  const [muteInput, setMuteInput] = useState('');
  const activeCategory = searchParams.get('cat');
  const activeFilter: 'unread' | 'all' = searchParams.get('filter') === 'all' ? 'all' : 'unread';
  const headerHidden = useAutoHideHeader(menuOpen);
  // unread count badge for the menu (shares the feed query cache)
  const unread = useClusters('unread', activeCategory ?? undefined);

  const feedUrl = (filter: 'unread' | 'all', cat: string | null): string => {
    const params = new URLSearchParams();
    if (filter === 'all') params.set('filter', 'all');
    if (cat) params.set('cat', cat);
    const query = params.toString();
    return query ? `/?${query}` : '/';
  };

  const categories = useMemo(() => {
    const set = new Set<string>();
    for (const feed of feeds.data ?? []) if (feed.category) set.add(feed.category);
    return [...set].sort();
  }, [feeds.data]);

  // sync server-side settings (shared across devices) to local presentation
  useEffect(() => {
    const s = settings.data;
    if (!s) return;
    document.body.className = document.body.className.replace(/font-size-\w+\s*/g, '');
    document.body.classList.add(`font-size-${s.fontSize}`);
    if (i18n.language !== s.language) void i18n.changeLanguage(s.language);
    if (getStoredTheme() !== s.theme) applyTheme(s.theme);
  }, [settings.data, i18n]);

  // close the drawer on navigation
  useEffect(() => setMenuOpen(false), [location.pathname, location.search]);

  const toggleTheme = () => {
    const isDark = document.documentElement.classList.contains('dark');
    applyTheme(isDark ? 'light' : 'dark');
  };

  // the grid layout gets a wider column on desktop (more cards per row); other pages keep the reader width
  const wide = location.pathname === '/' && settings.data?.feedLayout === 'grid';
  const containerWidth = wide ? 'max-w-6xl' : 'max-w-3xl';

  return (
    <div className={`mx-auto flex min-h-dvh flex-col ${containerWidth}`}>
      <header
        className={`fixed inset-x-0 top-0 z-40 mx-auto flex h-14 ${containerWidth} items-center gap-1 border-b border-neutral-200/60 bg-white/70 px-3 backdrop-blur-xl transition-transform duration-300 dark:border-neutral-800/60 dark:bg-neutral-950/70 ${
          headerHidden ? '-translate-y-full' : 'translate-y-0'
        }`}
      >
        <NavLink to="/" className="flex items-center gap-2 px-1" aria-label={t('app.name')}>
          <img src="/icons/logo.svg" alt="" className="h-7 w-7" />
          <span className="text-lg font-bold tracking-tight">b4b</span>
        </NavLink>
        <div className="flex-1" />
        <button
          onClick={toggleTheme}
          aria-label={t('settings.theme')}
          className="flex min-h-[44px] min-w-[44px] items-center justify-center rounded-full hover:bg-neutral-200/60 dark:hover:bg-neutral-800/60"
        >
          <span className="dark:hidden">🌙</span>
          <span className="hidden dark:inline">☀️</span>
        </button>
        <button
          onClick={() => setMenuOpen((v) => !v)}
          aria-label="Menu"
          aria-expanded={menuOpen}
          className="flex min-h-[44px] min-w-[44px] items-center justify-center rounded-full text-xl hover:bg-neutral-200/60 dark:hover:bg-neutral-800/60"
        >
          ☰
        </button>
      </header>

      {/* slide-over drawer */}
      <div
        className={`fixed inset-0 z-40 bg-black/30 backdrop-blur-sm transition-opacity duration-300 ${
          menuOpen ? 'opacity-100' : 'pointer-events-none opacity-0'
        }`}
        onClick={() => setMenuOpen(false)}
        aria-hidden
      />
      <nav
        className={`fixed bottom-0 right-0 top-0 z-50 flex w-72 max-w-[85vw] flex-col border-l border-neutral-200/60 bg-white/85 backdrop-blur-2xl transition-transform duration-300 dark:border-neutral-800/60 dark:bg-neutral-950/85 ${
          menuOpen ? 'translate-x-0' : 'translate-x-full'
        }`}
      >
        <div className="flex items-center gap-2 px-5 pb-2 pt-5">
          <img src="/icons/logo.svg" alt="" className="h-7 w-7" />
          <div>
            <p className="font-bold leading-tight">b4b</p>
            <p className="text-xs text-neutral-500">{t('app.tagline')}</p>
          </div>
        </div>

        <div className="flex-1 overflow-y-auto px-3 py-2">
          {[...navItems, ...(isAdmin ? ([{ to: '/admin', key: 'nav.admin', icon: '📊' }] as const) : [])].map((item) => (
            <NavLink
              key={item.to}
              to={item.to}
              className={({ isActive }) =>
                `mb-1 flex min-h-[48px] items-center gap-3 rounded-2xl px-4 text-[15px] transition ${
                  isActive
                    ? 'bg-accent/10 font-semibold text-accent'
                    : 'hover:bg-neutral-100 dark:hover:bg-neutral-800/70'
                }`
              }
            >
              <span aria-hidden className="text-lg">
                {item.icon}
              </span>
              {t(item.key)}
            </NavLink>
          ))}

          <div className="mt-4">
            <p className="px-4 pb-2 text-xs font-semibold uppercase tracking-wider text-neutral-400">
              {t('feed.title')}
            </p>
            <div className="flex gap-1.5 px-4">
              {(['unread', 'all'] as const).map((f) => (
                <button
                  key={f}
                  onClick={() => navigate(feedUrl(f, activeCategory))}
                  className={`rounded-full px-3.5 py-1.5 text-xs transition ${
                    activeFilter === f
                      ? 'bg-accent font-medium text-white'
                      : 'bg-neutral-100 text-neutral-600 hover:bg-neutral-200 dark:bg-neutral-800 dark:text-neutral-300 dark:hover:bg-neutral-700'
                  }`}
                >
                  {t(`feed.filters.${f}`)}
                  {f === 'unread' && unread.data ? ` (${unread.data.unreadCount})` : ''}
                </button>
              ))}
            </div>
          </div>

          <div className="mt-4">
            <p className="px-4 pb-2 text-xs font-semibold uppercase tracking-wider text-neutral-400">
              {t('feed.layoutTitle')}
            </p>
            <div className="flex flex-wrap gap-1.5 px-4">
              {feedLayoutSchema.options.map((layout) => (
                <button
                  key={layout}
                  data-layout-option={layout}
                  aria-pressed={(settings.data?.feedLayout ?? 'reels') === layout}
                  onClick={() => {
                    updateSettings.mutate({ feedLayout: layout });
                    setMenuOpen(false);
                    if (location.pathname !== '/') navigate('/');
                  }}
                  className={`rounded-full px-3.5 py-1.5 text-xs transition ${
                    (settings.data?.feedLayout ?? 'reels') === layout
                      ? 'bg-accent font-medium text-white'
                      : 'bg-neutral-100 text-neutral-600 hover:bg-neutral-200 dark:bg-neutral-800 dark:text-neutral-300 dark:hover:bg-neutral-700'
                  }`}
                >
                  {t(`feed.layout.${layout}`)}
                </button>
              ))}
            </div>
            {(settings.data?.hiddenTopics.length ?? 0) > 0 && (
              <>
                <p className="px-4 pb-1 pt-3 text-[11px] text-neutral-400">{t('feed.hiddenTopics')}</p>
                <div className="flex flex-wrap gap-1.5 px-4">
                  {settings.data!.hiddenTopics.map((topic) => (
                    <button
                      key={topic}
                      onClick={() =>
                        updateSettings.mutate({
                          hiddenTopics: settings.data!.hiddenTopics.filter((item) => item !== topic),
                        })
                      }
                      title={t('feed.showTopic', { topic: topicLabel(topic) })}
                      className="rounded-full bg-neutral-100 px-3 py-1 text-xs text-neutral-600 hover:bg-neutral-200 dark:bg-neutral-800 dark:text-neutral-300 dark:hover:bg-neutral-700"
                    >
                      {topicLabel(topic)} ✕
                    </button>
                  ))}
                </div>
              </>
            )}
          </div>

          {categories.length > 0 && (
            <div className="mt-4">
              <p className="px-4 pb-2 text-xs font-semibold uppercase tracking-wider text-neutral-400">
                {t('sources.category')}
              </p>
              <div className="flex flex-wrap gap-1.5 px-4">
                <button
                  onClick={() => navigate(feedUrl(activeFilter, null))}
                  className={`rounded-full px-3.5 py-1.5 text-xs transition ${
                    !activeCategory
                      ? 'bg-accent font-medium text-white'
                      : 'bg-neutral-100 text-neutral-600 hover:bg-neutral-200 dark:bg-neutral-800 dark:text-neutral-300 dark:hover:bg-neutral-700'
                  }`}
                >
                  {t('feed.filters.all')}
                </button>
                {categories.map((cat) => (
                  <button
                    key={cat}
                    onClick={() => navigate(feedUrl(activeFilter, cat))}
                    className={`rounded-full px-3.5 py-1.5 text-xs transition ${
                      activeCategory === cat
                        ? 'bg-accent font-medium text-white'
                        : 'bg-neutral-100 text-neutral-600 hover:bg-neutral-200 dark:bg-neutral-800 dark:text-neutral-300 dark:hover:bg-neutral-700'
                    }`}
                  >
                    {cat}
                  </button>
                ))}
              </div>
            </div>
          )}

          <div className="mt-4">
            <p className="px-4 pb-1 text-xs font-semibold uppercase tracking-wider text-neutral-400">
              {t('settings.mutes')}
            </p>
            <p className="px-4 pb-2 text-[11px] text-neutral-400">{t('settings.mutesHint')}</p>
            <form
              onSubmit={(e) => {
                e.preventDefault();
                const value = muteInput.trim();
                if (value.length >= 2)
                  addMute.mutate({ keyword: value }, { onSuccess: () => setMuteInput('') });
              }}
              className="flex gap-1.5 px-4"
            >
              <input
                value={muteInput}
                onChange={(e) => setMuteInput(e.target.value)}
                placeholder={t('settings.mutePlaceholder')}
                className="min-w-0 flex-1 rounded-xl border border-neutral-200 bg-white/70 px-3 py-1.5 text-xs dark:border-neutral-700 dark:bg-neutral-900/70"
              />
              <button type="submit" className="rounded-xl bg-accent px-3 py-1.5 text-xs font-medium text-white">
                +
              </button>
            </form>
            {(mutes.data?.length ?? 0) > 0 && (
              <div className="flex flex-wrap gap-1.5 px-4 pt-2">
                {mutes.data!.map((mute) => (
                  <button
                    key={mute.id}
                    onClick={() => deleteMute.mutate(mute.id)}
                    title={t('settings.delete')}
                    className="rounded-full bg-neutral-100 px-3 py-1 text-xs text-neutral-600 hover:bg-red-50 hover:text-red-600 dark:bg-neutral-800 dark:text-neutral-300 dark:hover:bg-red-950"
                  >
                    {mute.keyword} ✕
                  </button>
                ))}
              </div>
            )}
          </div>
        </div>

        <InviteCard />

        <button
          onClick={() => {
            setMenuOpen(false);
            logout.mutate(undefined, { onSuccess: () => navigate('/login') });
          }}
          className="flex min-h-[52px] items-center gap-3 border-t border-neutral-200/60 px-7 text-[15px] hover:bg-neutral-100 dark:border-neutral-800/60 dark:hover:bg-neutral-800/70"
          style={{ paddingBottom: 'env(safe-area-inset-bottom)' }}
        >
          <span aria-hidden className="text-lg">
            🚪
          </span>
          {t('nav.logout')}
        </button>
      </nav>

      <main className="flex-1">
        <Outlet />
      </main>
    </div>
  );
}

/** Drawer card with the user's personal invite code: copy the code, share the link, see N/M used. */
function InviteCard() {
  const { t } = useTranslation();
  const invites = useInvites();
  const [copied, setCopied] = useState<'code' | 'link' | null>(null);
  const info = invites.data;
  if (!info || info.max === 0) return null;
  const exhausted = info.used >= info.max;

  const copy = async (what: 'code' | 'link') => {
    try {
      await navigator.clipboard.writeText(what === 'code' ? info.code : info.link);
      setCopied(what);
      setTimeout(() => setCopied(null), 1500);
    } catch {
      /* clipboard unavailable (insecure context) — the code stays visible for manual copy */
    }
  };
  const share = async () => {
    const text = t('invites.shareText', { code: info.code });
    if (navigator.share) {
      try {
        await navigator.share({ text, url: info.link });
        return;
      } catch {
        /* user cancelled the share sheet */
      }
    } else {
      await copy('link');
    }
  };

  return (
    <div className="border-t border-neutral-200/60 px-5 py-3 dark:border-neutral-800/60" data-testid="invite-card">
      <div className="flex items-center justify-between">
        <p className="text-xs font-semibold uppercase tracking-wider text-neutral-400">{t('invites.title')}</p>
        <span
          className={`rounded-full px-2 py-0.5 text-xs font-medium ${
            exhausted ? 'bg-neutral-200 text-neutral-500 dark:bg-neutral-800' : 'bg-accent/10 text-accent'
          }`}
          aria-label={t('invites.used', { used: info.used, max: info.max })}
        >
          {info.used}/{info.max}
        </span>
      </div>
      <p className="mt-1 text-xs text-neutral-500">{exhausted ? t('invites.exhausted') : t('invites.hint')}</p>
      <div className="mt-2 flex items-center gap-2">
        <code className="flex-1 rounded-xl bg-neutral-100 px-3 py-2 font-mono text-sm tracking-wider dark:bg-neutral-800">
          {info.code}
        </code>
        <button
          type="button"
          onClick={() => void copy('code')}
          className="min-h-[40px] rounded-xl border border-neutral-200 px-3 text-xs font-medium hover:bg-neutral-100 dark:border-neutral-700 dark:hover:bg-neutral-800"
        >
          {copied === 'code' ? t('invites.copied') : t('invites.copy')}
        </button>
        <button
          type="button"
          onClick={() => void share()}
          disabled={exhausted}
          className="min-h-[40px] rounded-xl bg-accent px-3 text-xs font-medium text-white hover:bg-accent-strong disabled:opacity-50"
        >
          {copied === 'link' ? t('invites.copied') : t('invites.share')}
        </button>
      </div>
    </div>
  );
}
